import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Intent, Icon } from '@blueprintjs/core';
import { AgGridReact } from '@ag-grid-community/react';
import { GetLocaleTextParams } from 'aggrid/agGrid.types';
import { TranslationKey } from 'i18n/i18n.types';
import cloneDeep from 'lodash.clonedeep';
import {
  CellValueChangedEvent,
  GridApi,
  GridReadyEvent,
} from '@ag-grid-enterprise/all-modules';
import sipPrefixColumnDefs from 'routes/SipSetting/templates/sipSettingPrefixCollDefs';
import * as types from 'data/sipSetting/sipSetting.types';
import {
  adaptSipSettingOperation,
  removeNewMoWithDeleteOperation,
  adaptSipPrefixRowData,
  adaptToRemoveTemporaryId,
  getIncrementalId,
} from 'routes/SipSetting/utils/SipSetting.adapters';
import * as utils from '../../utils/SipPrefixGrid.utils';
import EmptyData from '../EmptyData';
import ActionCellRenderer from './ActionCellRenderer';
import CodeCellRenderer from './CodeCellRenderer';
import './sip-prefix-list-form.scss';

const components = {
  actionCellRenderer: ActionCellRenderer,
  codeCellRenderer: CodeCellRenderer
};

const defaultColDef = {
  flex: 1,
  editable: false,
  floatingFilter: true,
};

let currentPrefixListIpPk = 0;
let idContoller = 1;

type PrefixRowVersioned = {
  rowKey: number,
  actions?: string,
} & types.SipPrefix;

type Props = {
  name: string,
  data: types.SipPrefix[],
  isEditing?: boolean,
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: (keepOperation: boolean) => types.SipPrefix[]
}

export const SipPrefixListForm = forwardRef<Ref, Props>(({
  data,
  isEditing,
}: Props, ref) => {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi>();

  const [sipPrefixes, setSipPrefixes] = useState<PrefixRowVersioned[]>([]);
  const [sipPrefixOccurrences, setAccessFunctionOccurrences] = useState<{ [key: string]: number }>({});
  const [changedPrefixes, setChangedPrefixes] = useState<{ [key: string]: boolean }>({});
  const [hasCodeError, setCodeError] = useState<boolean>(false);
  const [hasNonUniqueError, setNonUniqueError] = useState<boolean>(false);

  useEffect(() => {
    const clonedData = cloneDeep(data as PrefixRowVersioned[]);
    setSipPrefixes(clonedData);
  }, [data]);

  const isValid = () => !hasCodeError && !hasNonUniqueError;

  const getValues = (keepOperation: boolean) => {
    const clonedData = cloneDeep(sipPrefixes);

    return clonedData
        .map((item) => adaptSipSettingOperation<PrefixRowVersioned>(item, changedPrefixes, keepOperation))
        .filter((item) => removeNewMoWithDeleteOperation<PrefixRowVersioned>(item))
        .map((item) => adaptToRemoveTemporaryId<PrefixRowVersioned>(item, keepOperation));
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const handleGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  const handleCellValueChanged = (event: CellValueChangedEvent) => {
    if (!utils.cellValueHasChanged(event) || !gridApi) {
      return;
    }

    if (utils.isChangingColumn('code', event)) {
      const newSipPrefixOccurrences = utils.updateSipPrefixOccurrencess(
        sipPrefixOccurrences,
        event?.oldValue,
        event?.newValue,
      );

      setAccessFunctionOccurrences(newSipPrefixOccurrences);

      const nonUniqueError = utils.hasRepeatedSipPrefixOccurrencesError(newSipPrefixOccurrences);
      event.context.sipPrefixOccurrences = newSipPrefixOccurrences;

      setNonUniqueError(nonUniqueError);
      setSipPrefixes(utils.getRowData<PrefixRowVersioned>(gridApi));
      setChangedPrefixes((current) => ({ ...current, [event.data.id]: true }));
    }
  }

  const handleAddPrefix = () => {
    if (!gridApi) {
      return;
    }

    const newRowData = utils.addNewItemToGridRowData<PrefixRowVersioned>(gridApi, {
      id: `DX_PREFIX_ID_${idContoller}`,
      pid: getIncrementalId<types.SipPrefix>(
        [...utils.getRowData<PrefixRowVersioned>(gridApi), ...data],
        'pid'
      ),
      code: '',
      actions: '',
      rowKey: currentPrefixListIpPk += 1,
      operation: 'CREATE'
    });

    idContoller +=1;

    setSipPrefixes(newRowData);
  };

  const handleRemoveIp = (id: string) => {
    const clonedData = cloneDeep(sipPrefixes).map((item, index) => {
      if (item.id === id) {
        setChangedPrefixes((current) => ({ ...current, [id]: true }));

        return {
          ...item,
          operation: 'DELETE'
        }
      }

      return item;
    });

    setSipPrefixes(clonedData as PrefixRowVersioned[]);
  }

  const localeText = ({ key, defaultValue }: GetLocaleTextParams): string => {
    return t(`agGrid.${key}` as TranslationKey, defaultValue);
  };

  const getRowData = (prefixData: PrefixRowVersioned[]) => {
    return prefixData.filter((item) => item.operation !== 'DELETE');
  }

  const renderAddPrefixButton = (): JSX.Element | null => {

    if (!isEditing) {
      return null;
    }

    return (
      <Button
        data-testid="addDeviceButton"
        minimal
        intent={Intent.PRIMARY}
        icon="add"
        text={t('sipSetting.form.newPrefix')}
        onClick={handleAddPrefix}
      />
    );
  }

  const renderPrefixList = (): JSX.Element | null => {
    if (!getRowData(sipPrefixes)?.length && !isEditing) {
      return <EmptyData message={t('sipSetting.form.noPrefix')} />
    }

    return (
      <div className="ag-theme-balham sip-prefix-grid">
        <AgGridReact
          stopEditingWhenGridLosesFocus
          columnDefs={sipPrefixColumnDefs}
          rowData={getRowData(sipPrefixes)}
          components={components}
          defaultColDef={defaultColDef}
          context={{
            sipPrefixes,
            sipPrefixOccurrences,
            isEditing,
            onCodeValidationError: setCodeError,
            onRemoveIp: handleRemoveIp,
          }}
          onCellValueChanged={handleCellValueChanged}
          onGridReady={handleGridReady}
          getLocaleText={localeText}
        />
      </div>
    );
  }

  return (
    <div className="sip-prefix-list-form" data-testid="SipPrefixListForm">
      {renderAddPrefixButton()}
      {renderPrefixList()}
    </div>
  );
});

export default SipPrefixListForm;
